import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';

import { useGetCurrentUserQuery } from '../../store/api/user';
import { useGetWorkspacesQuery } from '../../store/api/workspaces';
import { setAvatarCompany, setCompanyName, setUserRole } from '../../store/slices/auth';
import { useGetCropsQuery } from '../../store/api/catalog';
import { getCropsList } from '../../store/slices/catalog';
import { setCurrentWorkspaceId } from '../../store/slices/workspaces';
import { statusWelcomeModal, statusWorkspaceModal } from '../../store/slices/ui';
import { AddWorkspaceModal, QRCodeDeviceId, Workspace } from '../../constants/keys';
import { useLocalStorage, useViewport, useLogout } from '../../hooks';
import { Loading, WorkspaceModal } from '../../components';
import { Organization } from '../WorkspaceModal/Organization.jsx';
import { Nav } from './index.jsx';
import { BreakpointTabletUp, PathnamesMap } from '../../constants/ui';
import { DevicesQRCodeLinkKey } from '../Devices/constants/index.jsx';
import { getCropName } from '../../utils/crops';

const PageLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { widthViewport } = useViewport();

  const [workspaceId, setWorkspaceId] = useLocalStorage(Workspace, '');
  const [workspaceModalActive] = useLocalStorage(AddWorkspaceModal, '');

  const { pathname } = useLocation();
  const { welcomeModalStatus, workspaceModalStatus } = useSelector((state) => state.ui);
  const { currentWorkspaceId } = useSelector((state) => state.workspaces);

  const [newWorkspaceId, setNewWorkspaceId] = useState('');

  const { isSuccess: isUserSuccess, isError: isUserError, data: userData } = useGetCurrentUserQuery();
  const { isSuccess: isWorkspacesSuccess, data: workspaces = [] } = useGetWorkspacesQuery(
    {},
    { skip: !userData || isUserError, refetchOnMountOrArgChange: true },
  );
  const { isSuccess: isCatalogCropsSuccess, data: catalogCrops } = useGetCropsQuery(
    {},
    { skip: !userData || isUserError, refetchOnMountOrArgChange: true },
  );

  const setWorkspace = useCallback(
    ({ id, name, avatar }) => {
      dispatch(setCurrentWorkspaceId({ workspaceId: id }));
      dispatch(setCompanyName({ userCompany: name }));
      dispatch(setAvatarCompany({ avatar: avatar }));
    },
    [dispatch, setWorkspaceId],
  );

  const isSuccess = useMemo(() => isUserSuccess && isWorkspacesSuccess, [isUserSuccess, isWorkspacesSuccess]);

  const logoutUser = useLogout();

  useEffect(() => {
    if (isUserError) {
      logoutUser().then();
    }

    if (isSuccess && pathname === '/') {
      const newDeviceId = localStorage.getItem(QRCodeDeviceId);
      if (newDeviceId) {
        // Redirect to 'Add device' when scanning the QR code
        navigate(`/${PathnamesMap.devices}?${DevicesQRCodeLinkKey}=${newDeviceId}`, { replace: true });
      } else {
        navigate('/sensors', { replace: true });
      }
    }
  }, [dispatch, isSuccess, isUserError, navigate, pathname]);

  //set workspace ID and company info
  useEffect(() => {
    if (isWorkspacesSuccess && workspaces.length) {
      if (!workspaceId || !workspaces.find((item) => item.id === workspaceId)) {
        const [{ id, name, avatar }] = workspaces;
        localStorage.setItem(Workspace, JSON.stringify(id));
        // if ('serviceWorker' in navigator) {
        //   navigator.serviceWorker.ready.then((registration) => {
        //     registration.active.postMessage({
        //       type: 'WORKSPACE_ID',
        //       workspaceId: id,
        //     });
        //   });
        // }
        setWorkspace({ id, name, avatar });
        dispatch(setCurrentWorkspaceId({ workspaceId: id }));
      } else if (workspaceId && !currentWorkspaceId) {
        const workspace = workspaces.filter((item) => item.id === workspaceId)[0];
        dispatch(setCurrentWorkspaceId({ workspaceId: workspace.id }));
        dispatch(setCompanyName({ userCompany: workspace.name }));
        dispatch(setAvatarCompany({ avatar: workspace.avatar }));
      }
    }

    if (isWorkspacesSuccess) {
      if (workspaceModalActive !== 'close') {
        // if is_demo show modal only once
        dispatch(
          statusWelcomeModal(
            (!workspaces.length && !workspaceId) || (workspaces.length === 1 && workspaces[0].is_demo),
          ),
        );
      } else {
        dispatch(statusWelcomeModal(workspaces.length === 0));
      }
    }
  }, [isWorkspacesSuccess, workspaceId]);

  useEffect(() => {
    // after close workspace modal
    if (newWorkspaceId) {
      // if ('serviceWorker' in navigator) {
      //   navigator.serviceWorker.ready.then((registration) => {
      //     registration.active.postMessage({
      //       type: 'WORKSPACE_ID',
      //       workspaceId: id,
      //     });
      //   });
      // }
      setTimeout(() => {
        const newDeviceId = localStorage.getItem(QRCodeDeviceId);
        // Redirect to 'Add device' when scanning the QR code or to Dashboard
        window.location.href = newDeviceId
          ? `/${PathnamesMap.devices}?${DevicesQRCodeLinkKey}=${newDeviceId}`
          : `/${PathnamesMap.dashboard}`;
      }, 120);
    }
  }, [newWorkspaceId]);

  useEffect(() => {
    if (currentWorkspaceId) {
      const membership = userData.workspace_memberships.filter((item) => item.workspace.id === currentWorkspaceId)[0];
      dispatch(setUserRole({ userRole: membership.role }));
    }
  }, [currentWorkspaceId]);

  //set crops list
  useEffect(() => {
    if (isCatalogCropsSuccess) {
      const crops = catalogCrops.map((crop) => {
        return {
          ...crop,
          label: getCropName({ t, id: crop.id }),
        };
      });
      dispatch(getCropsList(crops));
    }
  }, [catalogCrops, dispatch, isCatalogCropsSuccess]);

  return (
    <div
      className={`app${
        pathname.includes(`/${PathnamesMap.timeline}`) ||
        pathname.includes(`/${PathnamesMap.vegetation}`) ||
        pathname.includes(`/${PathnamesMap.charts}`) ||
        pathname.includes(`/${PathnamesMap.photo}`) ||
        pathname.includes('/map') ||
        pathname.includes('/devices') ||
        pathname.includes('/workspaces') ||
        pathname.includes('/profile') ||
        pathname.includes('/fields') ||
        pathname.includes('/season') ||
        pathname.includes('/members')
          ? ' app--full-mobile'
          : ''
      }
        ${
          pathname.includes(`/${PathnamesMap.timeline}`) ||
          pathname.includes(`/${PathnamesMap.vegetation}`) ||
          pathname.includes(`/${PathnamesMap.charts}`) ||
          pathname.includes(`/${PathnamesMap.photo}`) ||
          pathname.includes('/season')
            ? 'app--no-nav-mobile'
            : ''
        }`}
    >
      {isUserSuccess && (
        <>
          {!(
            widthViewport < BreakpointTabletUp &&
            (pathname.includes(`/${PathnamesMap.timeline}`) ||
              pathname.includes(`/${PathnamesMap.charts}`) ||
              pathname.includes(`/${PathnamesMap.vegetation}`) ||
              pathname.includes(`/${PathnamesMap.photo}`) ||
              pathname.includes('/season'))
          ) && <Nav widthViewport={widthViewport} />}

          {!isSuccess ? <Loading /> : <Outlet />}

          {welcomeModalStatus && (
            <WorkspaceModal
              isOnlyDemoWorkspace={workspaces.length === 1 && workspaces[0].is_demo}
              setWorkspaceId={setNewWorkspaceId}
              t={t}
            />
          )}

          {workspaceModalStatus && (
            <Modal
              open={workspaceModalStatus}
              onCancel={() => dispatch(statusWorkspaceModal(false))}
              footer={false}
              centered
              width={450}
            >
              <Organization
                setActiveModal={() => dispatch(statusWorkspaceModal(false))}
                showOnlyCreateForm={true}
                t={t}
              />
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default PageLayout;
